<template>
  <a-config-provider
    :locale="locale"
    :theme="{
      token: {
        colorPrimary: tenantColor,
        colorInfo: tenantColor,
        fontSize: 14,
        colorLink: tenantColor,
        fontFamily: 'Roboto, Helvetica Neue, Arial, Noto Sans, sans-serif',
        borderRadius: 12,
      },
    }"
  >
    <a-layout style="min-height: 100vh">
      <a-layout-sider
        v-if="isAuthenticated"
        ref="siderRef"
        :width="340"
        :trigger="null"
        :style="{ zIndex: 10, height: '99vh', left: 0, top: 0, bottom: 0, backgroundColor: '#f0f2f5' }"
        v-model:collapsed="collapsed"
        collapsedWidth="40px"
        collapsible
        :class="`${collapsed ? ' sidebar_collapsed' : 'sidebar'}`"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
      >
        <div v-if="isSidebarPinned" class="sidebar-toggle-button" @click="collapsed = !collapsed">
          {{ collapsed ? "▶" : "◀" }}
        </div>

        <div style="display: flex; flex-direction: column; justify-content: space-between; height: 100%">
          <div class="logo_new" style="margin-left: 12px; margin-top: 12px; display: flex; flex-direction: row; justify-content: space-between">
            <router-link :to="`/llm`" @click="eventBus.emit('on-llm-home')">
              <img v-if="collapsed" class="logo-image" style="height: 34px; width: auto" :src="logoSmallSrc" />
              <img v-else class="logo-image" style="width: 200px" :src="logoSrc" />
            </router-link>
            <a-tooltip v-if="!collapsed">
              <template #title>{{ isSidebarPinned ? "Sidebar abheften" : "Sidebar anheften" }}</template>
              <a-button shape="circle" :type="isSidebarPinned ? 'primary' : 'dashed'" @click="togglePin">
                <template #icon> <PushpinOutlined /></template>
              </a-button>
            </a-tooltip>
          </div>

          <LLMDocumentBrowser v-if="isAuthenticated && route?.name === 'Large Language Model'"></LLMDocumentBrowser>
          <navigation @dropdownToggle="onDropdownToggle" :collapsed="collapsed" />
        </div>
      </a-layout-sider>
      <a-layout>
        <a-layout-content :style="isFullwidthPage ? 'display: flex; flex-direction: column' : 'padding: 0px; display: flex; flex-direction: column'">
          <div :class="isFullwidthPage ? 'llm_wrapper' : 'width_wrapper'">
            <a-layout style="padding: 0px; background: #fff; height: 100%; overflow: auto; flex-direction: column; scrollbar-width: thin">
              <router-view v-if="isAuthenticated || route.name === 'navigation.password'" />
              <a-layout-content :style="{ padding: '0 24px', minHeight: '480px', 'align-content': 'center' }" v-else>
                <a-row>
                  <a-col :span="12" :offset="6">
                    <a-card>
                      <login-form
                        ><template v-slot:message> <a-alert message="Error" description="Unable to login. Please check your credentials" type="error" /> </template
                      ></login-form>
                    </a-card>
                  </a-col>
                </a-row>
                <a-layout-footer style="text-align: center; padding: 8px 50px; background-color: #fff">Jaasper 2016-{{ new Date().getFullYear() }} Alle Rechte vorbehalten. </a-layout-footer>
              </a-layout-content>
            </a-layout>
          </div>
        </a-layout-content>
      </a-layout>
    </a-layout>
    <broadcast-modal v-if="isAuthenticated" />
  </a-config-provider>
</template>

<script lang="ts">
import dayjs from "dayjs";
import "dayjs/locale/de";
dayjs.locale("de");

import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watch, watchEffect } from "vue";
import Homepage from "./components/Homepage.vue";
import Navigation from "./components/Navigation.vue";

import deDE from "ant-design-vue/es/locale/de_DE";
import { useRoute, useRouter } from "vue-router";
import LLMDocumentBrowser from "./components/shared/LLMDocumentBrowser.vue";
import eventBus from "./components/workflow/eventBus";
import { useUserStore } from "./store/modules/user";
import LoginForm from "./components/LoginForm.vue";
import BroadcastModal from "./components/BroadcastModal.vue";
import { MenuFoldOutlined, PushpinOutlined, MenuUnfoldOutlined } from "@ant-design/icons-vue";
import { storeToRefs } from "pinia";

export default defineComponent({
  name: "App",

  setup() {
    const store = useUserStore();
    const loading = ref(true);
    const route = useRoute();
    const isFullwidthPage = ref(false);
    const isAuthenticated = computed(() => store.isAuthenticated);
    const { isSidebarPinned } = storeToRefs(store);
    const isMouseDown = ref(false);

    onMounted(() => {
      document.addEventListener("mousedown", () => {
        isMouseDown.value = true;
      });

      document.addEventListener("mouseup", () => {
        isMouseDown.value = false;
      });
    });

    onBeforeUnmount(() => {
      document.removeEventListener("mousedown", () => (isMouseDown.value = true));
      document.removeEventListener("mouseup", () => (isMouseDown.value = false));
    });

    const togglePin = () => {
      store.toggleSidebarPin();
      if (store.sidebarPinned) {
        collapsed.value = false;
      }
    };

    watch(
      () => route.path,
      () => {
        if (route.name) {
          isFullwidthPage.value = route.name == "Large Language Model" && isAuthenticated.value === true;
        }
      }
    );

    onMounted(() => {
      isFullwidthPage.value = route.name == "Large Language Model" && isAuthenticated.value === true;
    });

    watch(
      () => store.authStatus,
      () => {
        if (store.authStatus === "loading") {
          loading.value = true;
        } else {
          loading.value = false;
        }
      }
    );

    const router = useRouter();

    watchEffect(async () => {
      if (store.token && !store.isUserReady) {
        try {
          if (!route.name) {
            return;
          }

          const isPasswordResetPage = route.name === "navigation.password";

          if (!isPasswordResetPage && !isAuthenticated.value) {
            router.push("/");
          }
        } catch (e) {
          console.warn("User request failed", e);
        }
      }
    });

    const collapsed = ref(true);
    const siderRef = ref<HTMLElement | null>(null);

    const dropdownOpen = ref(false);

    let closeTimeout: ReturnType<typeof setTimeout> | null = null;

    const onMouseEnter = () => {
      if (!isSidebarPinned.value) collapsed.value = false;
      if (closeTimeout) {
        clearTimeout(closeTimeout);
        closeTimeout = null;
      }
    };

    const getSiderElement = () => {
      return siderRef.value?.$el as HTMLElement | null;
    };

    const waitToCloseSidebar = () => {
      if (isMouseDown.value) {
        setTimeout(waitToCloseSidebar, 100);
        return;
      }

      const siderElement = getSiderElement();
      if (siderElement && !siderElement.matches(":hover")) {
        collapsed.value = true;
      }
    };

    const onMouseLeave = (event: MouseEvent) => {
      if (dropdownOpen.value || isSidebarPinned.value) return;

      const siderElement = getSiderElement();
      if (siderElement && siderElement.contains(event.relatedTarget as Node)) {
        return;
      }

      closeTimeout = setTimeout(() => {
        waitToCloseSidebar();
      }, 10000);
    };

    const onDropdownToggle = (isOpen: boolean) => {
      dropdownOpen.value = isOpen;

      if (!isOpen) {
        nextTick(() => {
          setTimeout(() => {
            const siderElement = getSiderElement();
            if (siderElement && !siderElement.matches(":hover")) {
              collapsed.value = true;
            }
          }, 50);
        });
      }
    };

    const tenantColor = computed(() => {
      if (store.getTenantConfig.color && store.getTenantConfig.color.length > 0) {
        return store.getTenantConfig.color;
      }
      return "#8c0035";
    });

    // Get logos from store with fallback to default assets
    const logoSrc = computed(() => {
      return store.logoLarge || new URL("./assets/jaasper_logo.png", import.meta.url).toString();
    });

    const logoSmallSrc = computed(() => {
      return store.logoSmall || new URL("./assets/jaasper_logo_small.png", import.meta.url).toString();
    });

    return {
      logoSmallSrc,
      logoSrc,
      loading,
      locale: deDE,
      route,
      tenantColor,
      isFullwidthPage,
      onMouseEnter,
      onMouseLeave,
      dropdownOpen,
      onDropdownToggle,
      eventBus,
      isAuthenticated,
      selectedKeys1: ref<string[]>(["2"]),
      selectedKeys2: ref<string[]>(["1"]),
      collapsed,
      openKeys: ref<string[]>(["sub1"]),
      isSidebarPinned,
      togglePin,
      siderRef,
    };
  },
  components: {
    Homepage,
    Navigation,
    LoginForm,
    LLMDocumentBrowser,
    BroadcastModal,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    PushpinOutlined,
  },
});
</script>
<style scoped>
.logo {
  float: left;
  width: 180px;
  height: 30px;
  margin: auto 20px auto auto;
  background: rgba(255, 255, 255, 0.3);
}

.logo-image {
  width: 100%;
}
</style>
<style>
.ant-image-preview-img {
  max-height: unset !important;
}

#components-layout-demo-top-side-2 .logo {
  float: left;
  width: 120px;
  height: 31px;
  margin: 16px 24px 16px 0;
  background: rgba(255, 255, 255, 0.3);
}
.sidebar-toggle-button {
  position: absolute;
  top: 14px;
  left: calc(100% + 20px);
  transform: translateX(-10px);
  border: none;
  padding: 8px 12px;
  cursor: pointer;
  border-radius: 5px;
  font-size: 14px;
  font-weight: bold;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease, background 0.2s ease;
}

.sidebar-toggle-button:hover {
  transform: translateX(-5px); /* Slight movement on hover */
}

.ant-row-rtl #components-layout-demo-top-side-2 .logo {
  float: right;
  margin: 16px 0 16px 24px;
}

.site-layout-background {
  background: #fff;
}

.loading-container {
  height: 100vh;
}

.loading-placeholder {
  top: 50vh;
  margin: auto;
}

.loading-placeholder img {
  width: 300px;
}

.main-loading .anticon {
  margin: 20px auto auto auto;
}

.icon-holder {
  display: flex;
  align-items: center;
  justify-content: center;
}

#editor-sidebar .ant-layout-sider-trigger {
  background: #8c0035 !important;
}

#editor-sidebar {
  overscroll-behavior: contain;
}

.ant-layout-header {
  box-shadow: 3px 0 6px #ccc;
}

.ant-table-filter-dropdown {
  min-width: 150px !important;
}

html {
  overflow-x: hidden;
}

.bottom-seperator {
  margin: 48px 0;
}
.vue-flow__edge-path,
.vue-flow__connection-path {
  stroke: #8c0035;
  stroke-width: 2;
  fill: none;
}
#arrow polyline {
  stroke: #8c0035 !important;
}

/* main {
  height: 85vh;
} */

html,
body {
  height: 100%;
  margin: 0;
  padding: 0;
}
#app {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
}
.layout {
  width: 100%;
}
.width_wrapper {
  margin: 0 auto;
  width: 100%;
  height: 100%;
  margin-left: 0;
}
.width_wrapper > .ant-layout {
  padding-top: 24px !important;
}
.llm_wrapper {
  margin: 0 auto;
  width: 100%;
  height: 100%;
  padding: 0;
}
.llm_wrapper .actions_container {
  padding-top: 24px !important;
}
/* Ultra Wide screens */
</style>
<style>
.sidebar_collapsed .sidebar_dynamic_item {
  opacity: 0;
}
.sidebar_dynamic_item {
  transition: opacity 0.75s ease-in-out;
  overflow-x: hidden;
}
p {
  line-height: 1.5715;
}
.ant-collapse-header-text {
  display: flex;
}
.parent-container .ant-collapse-content-box {
  padding: 16px 28px !important;
}
</style>
